import { UserModel, ClientModel, ResourceModel, AddressModel } from '../modules/models';
import { getDefaultValue, getConditionalDefaultValue, sanitizePhoneNumber, isEmpty, sanitizeNumber } from './generalUtils';
import { sanitizeAddress } from './addressUtils';
import { CompanyLabel } from 'modules/models/client';

export const sanitizeUser = (user: UserModel.IUser) => {
  const data = {
    ...user,
    isWorker: user.isWorker || user.isWorkerChecked,
    firstName: getDefaultValue(user.firstName, null),
    lastName: getDefaultValue(user.lastName, null),
    title: getDefaultValue(user.title, null),
    email: getDefaultValue(user.email, null),
    preferredContactMethod: parseInt(user.preferredContactMethod as any, 10),
    mobilePhoneNumber: isEmpty(user.mobilePhoneNumber) ? null : sanitizePhoneNumber(user.mobilePhoneNumber),
    officePhoneNumber: isEmpty(user.officePhoneNumber) ? null : sanitizePhoneNumber(user.officePhoneNumber),
    officePhoneExtension: isEmpty(user.officePhoneExtension) ? null : sanitizePhoneNumber(user.officePhoneExtension),
    projects: user.projects?.map(({ value, id }) => ({ id: value || id })),
  };
  delete data.isWorkerChecked;
  return data;
};

export const sanitizeUserList = (userList: UserModel.IUser[]) => userList.filter(user => user.email).map(user => sanitizeUser(user));

export const sanitizeClient = (client: ClientModel.IClient, noneId) => ({
  ...client,
  taxpayerIdentificationNumber: !isEmpty(client.taxpayerIdentificationNumber) ? String(client.taxpayerIdentificationNumber) : null,
  mwbeTypeId: !isEmpty(client.mwbeTypeId) ? client.mwbeTypeId : noneId,
  billingAddress: isEmpty(client.billingAddress) ? AddressModel.getFallbackAddress() : sanitizeAddress(client.billingAddress),
  mailingAddress: /* istanbul ignore next */ client.mailingAddressMatchesBillingAddress
    ? null
    : client.mailingAddress
    ? sanitizeAddress(client.mailingAddress)
    : null,
  trades: client.trades.map(trade => trade.id) as any,
  users: client.users && sanitizeUserList([...client.users]),
  universalBadgePrice: getConditionalDefaultValue(client?.hasUniversalBadge, sanitizeNumber(client.universalBadgePrice), null),
  jointVentureCompanyIds: client.companyType === ClientModel.CompanyType.JointVenture ? client.jointVentureCompanies?.map(c => c.value) : [],
  taxCondition: client?.project?.id ? getTaxExemptValue(client.taxCondition) : null,
  phoneNumber: client.phoneNumber ? sanitizePhoneNumber(client.phoneNumber) : '',
});

export const preloadClient = (client: ClientModel.IClient) => ({
  ...client,
  billingAddress: isEmpty(client.billingAddress) ? AddressModel.getFallbackAddress() : client.billingAddress,
});

export const getDrawerButton = (status, clientId: string): { buttonText: string; linkTo: string; permissionsExpression: string } => {
  const drawerButton = {
    [ResourceModel.CompanyStatus.DRAFT]: {
      buttonText: 'Edit',
      linkTo: `/clients/wizard/${clientId}`,
      permissionsExpression: UserModel.DraftClientsPermission.MANAGE,
    },
    [ResourceModel.CompanyStatus.ACTIVE]: {
      buttonText: 'Company Detail',
      linkTo: `/clients/detail/${clientId}`,
      permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
    },
    [ResourceModel.CompanyStatus.PENDING_APPROVAL]: {
      buttonText: 'Review',
      linkTo: `/clients/wizard/${clientId}/review`,
      permissionsExpression: UserModel.DraftClientsPermission.MANAGE,
    },
    [ResourceModel.CompanyStatus.ARCHIVED]: {
      buttonText: 'Company Detail',
      linkTo: `/clients/detail/${clientId}`,
      permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
    },
    [ResourceModel.CompanyStatus.ONBOARDING]: {
      buttonText: 'Edit Company',
      linkTo: `/clients/invite/${clientId}`,
      permissionsExpression: UserModel.DraftClientsPermission.INVITE,
    },
    [ResourceModel.CompanyStatus.ONBOARDING_PENDING_APPROVAL]: {
      buttonText: 'Review',
      linkTo: `/clients/wizard/${clientId}/review`,
      permissionsExpression: UserModel.DraftClientsPermission.MANAGE,
    },
    [ResourceModel.CompanyStatus.REJECTED]: {
      buttonText: 'Company Detail',
      linkTo: null,
      permissionsExpression: UserModel.ClientsPermission.VIEWACCESS,
    },
  };
  return drawerButton[status] || { buttonText: '', linkTo: '', permissionsExpression: '' };
};

export const getTaxExemptValue = value => {
  if (value === 'exempt') {
    return true;
  } else if (value === 'non-exempt') {
    return false;
  } else {
    return null;
  }
};

export const getTaxExemptValueForModel = value => {
  if (value === true) {
    return 'exempt';
  } else if (value === false) {
    return 'non-exempt';
  } else {
    return '';
  }
};

export const formatHierarchy = (clientId: string, hierarchy: any, type: string) => {
  let nextType = type;
  if (hierarchy && hierarchy.id && hierarchy.id === clientId) {
    hierarchy.isCurrent = true;
    nextType = 'CHILD';
  } else {
    if (type === 'PARENT') {
      hierarchy.isParent = true;
    } else if (type === 'SIBILING') {
      hierarchy.isSibiling = true;
      nextType = 'SIBILINGCHILD';
    } else if (type === 'CHILD') {
      hierarchy.isChild = true;
    } else if (type === 'SIBILINGCHILD') {
      hierarchy.isSibilingChild = true;
    }
  }
  if (hierarchy && hierarchy.branches && hierarchy.branches.length > 0) {
    let currentIndex = hierarchy.branches.findIndex(h => h.id === clientId);
    if (currentIndex && currentIndex >= 0) {
      hierarchy.branches[currentIndex].isCurrent = true;
      nextType = 'SIBILING';
    }
    for (let i = 0; i < hierarchy.branches.length; i++) {
      formatHierarchy(clientId, hierarchy.branches[i], nextType);
    }
  }
};

export const getCurrentClientNode = hierarchy => {
  let result = null;
  if (hierarchy && hierarchy.isCurrent) {
    result = hierarchy;
  }
  if (!result && hierarchy && hierarchy.branches && hierarchy.branches.length > 0) {
    for (let i = 0; i < hierarchy.branches.length; i++) {
      if (!result) {
        result = getCurrentClientNode(hierarchy.branches[i]);
      }
      if (result) {
        break;
      }
    }
  }
  return result;
};

export const getChild = (clientId, hierarchy: any, branches: any) => {
  let result = branches;
  if (hierarchy && ((hierarchy.id && hierarchy.id === clientId) || hierarchy.isCurrent)) {
    result = hierarchy.branches;
    if (hierarchy.id === null) {
      hierarchy.id = clientId;
      hierarchy.clientId = null;
    }
    return result;
  } else {
    if (hierarchy && hierarchy.branches && hierarchy.branches.length > 0) {
      for (let i = 0; i < hierarchy.branches.length; i++) {
        let tempResult: any = getChild(clientId, hierarchy.branches[i], result);
        if (tempResult && tempResult.length > 0) {
          result = tempResult;
          // break;
          return result;
        }
      }
    }
  }
  return result;
};

export const getClients = (hierarchy: any) => {
  let clients = [];
  if (hierarchy && hierarchy.id) {
    clients.push({
      id: hierarchy.id,
      name: hierarchy.name,
      status: hierarchy.status,
      projectsCount: hierarchy.projectsCount,
      companyLabel: hierarchy.label,
      companyType: hierarchy.type,
      isVirtualBranch: hierarchy.isVirtualBranch,
      relationshipName: hierarchy.relationshipName,
      mwbeTypeId: hierarchy.mwbeTypeId,
      taxpayerIdentificationNumber: hierarchy.taxpayerIdentificationNumber,
    });
  }
  if (hierarchy && hierarchy.branches && hierarchy.branches.length > 0) {
    for (let i = 0; i < hierarchy.branches.length; i++) {
      const tempClients = getClients(hierarchy.branches[i]);
      clients = [...clients, ...tempClients];
    }
  }
  if (hierarchy?.ventureCompanies?.length > 0) {
    for (let i = 0; i < hierarchy.ventureCompanies.length; i++) {
      const tempClients = getClients(hierarchy.ventureCompanies[i]);
      clients = [...clients, ...tempClients];
    }
  }
  return clients;
};

export const isCurrentClientVirtualBranch = node => {
  let result = false;
  if (node && node.isCurrent) {
    result = node.isVirtualBranch ? true : false;
  }
  if (!result && node && node.branches && node.branches.length > 0) {
    for (let i = 0; i < node.branches.length; i++) {
      if (!result) {
        result = isCurrentClientVirtualBranch(node.branches[i]);
      }
      if (result) {
        break;
      }
    }
  }
  return result;
};

export const getClientLabel = (label: number, isFormated: boolean = true) => {
  let result = '';
  switch (label) {
    case CompanyLabel.HO:
      result = isFormated ? ' (HO)' : 'HO';
      break;
    case CompanyLabel.B:
      result = isFormated ? ' (B)' : 'B';
      break;
    case CompanyLabel.SB:
      result = isFormated ? ' (SB)' : 'SB';
      break;
    case CompanyLabel.JV:
      result = isFormated ? ' (JV)' : 'JV';
      break;
    default:
      result = '';
      break;
  }
  return result;
};

export const getGroupSearchQuery = (clientId: string) => {
  return {
    paging: {
      clientSidePagination: true,
    },
    expand: ['company', 'metadata', 'parentGroup', 'labels'],
    searchParameters: [
      {
        field: 'companyId',
        operator: 'equals',
        value: clientId,
      },
      {
        field: 'type',
        operator: 'notequals',
        value: 'Project',
      },
      {
        condition: 'or',
        searchParameters: [
          {
            field: 'hasAppAccessWebPermission',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasAppAccessJobSitePermission',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasAppAccessWorkerPermission',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasAppAccessTabletExperiencePermission',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'Type',
            operator: 'eq',
            value: 'Custom',
          },
        ],
      },
      {
        condition: 'or',
        searchParameters: [
          {
            field: 'hasAdminLabel',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasUserLabel',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasOSRLabel',
            operator: 'eq',
            value: 'true',
          },
          {
            field: 'hasWorkerLabel',
            operator: 'eq',
            value: 'true',
          },
        ],
      },
    ],
  };
};

export const loadMissingGroupIds = (user, groupList) => {
  let result = { ...user };
  if (groupList) {
    if (
      user?.companyUserAccessData &&
      user?.companyUserAccessData.companyGroupProjects &&
      user?.companyUserAccessData.companyGroupProjects.filter(cgp => (!cgp.groupId || cgp.groupId.length < 1) && (cgp.isAdmin || cgp.isUser)).length > 0
    ) {
      let tempCompanyUserAccessData = { ...user?.companyUserAccessData };
      tempCompanyUserAccessData.companyGroupProjects = tempCompanyUserAccessData.companyGroupProjects.map(cgp => {
        if ((!cgp.groupId || cgp.groupId.length < 1) && (cgp.isAdmin || cgp.isUser) && groupList && groupList.length) {
          const userGroup = groupList.find(
            uig =>
              uig.companyId === tempCompanyUserAccessData.companyId &&
              ((cgp.isAdmin && uig.code && uig.code.toLowerCase() === 'admin' && uig.hasAdminLabel) ||
                (cgp.isUser && uig.code && uig.code.toLowerCase() === 'user' && uig.hasUserLabel))
          );
          if (userGroup && userGroup.id) {
            return { ...cgp, groupId: userGroup.id };
          } else {
            return cgp;
          }
        } else {
          return cgp;
        }
      });
      result = { ...result, companyUserAccessData: tempCompanyUserAccessData };
    }
    if (
      user?.companyUserImpersonateAccessList &&
      user?.companyUserImpersonateAccessList.filter(
        cuia =>
          cuia.companyGroupProjects &&
          cuia.companyGroupProjects.filter(cgp => (!cgp.groupId || cgp.groupId.length < 1) && (cgp.isAdmin || cgp.isUser)).length > 0
      ).length > 0
    ) {
      let tempCompanyUserImpersonateAccessList = [...user?.companyUserImpersonateAccessList];
      tempCompanyUserImpersonateAccessList = tempCompanyUserImpersonateAccessList.map(cuia => {
        if (
          cuia.companyGroupProjects &&
          cuia.companyGroupProjects.filter(cgp => (!cgp.groupId || cgp.groupId.length < 1) && (cgp.isAdmin || cgp.isUser)).length > 0
        ) {
          let tempCompanyGroupProjects = [...cuia.companyGroupProjects];
          tempCompanyGroupProjects = tempCompanyGroupProjects.map(cgp => {
            if ((!cgp.groupId || cgp.groupId.length < 1) && (cgp.isAdmin || cgp.isUser) && groupList && groupList.length) {
              const userGroup = groupList.find(
                uig =>
                  uig.companyId === cuia.companyId &&
                  ((cgp.isAdmin && uig.code && uig.code.toLowerCase() === 'admin' && uig.hasAdminLabel) ||
                    (cgp.isUser && uig.code && uig.code.toLowerCase() === 'user' && uig.hasUserLabel))
              );
              if (userGroup && userGroup.id) {
                return { ...cgp, groupId: userGroup.id };
              } else {
                return cgp;
              }
            } else {
              return cgp;
            }
          });
          return { ...cuia, companyGroupProjects: tempCompanyGroupProjects };
        } else {
          return cuia;
        }
      });
      result = { ...result, companyUserImpersonateAccessList: tempCompanyUserImpersonateAccessList };
    }
  }
  return result;
};

export const generateJointVentureUsersPayload = (oldUserList: UserModel.IUser[] = [], newUserList: UserModel.IUser[] = []) => {
  const payload = { impersonatedAdminUsers: [], toDeleteImpersonatedAdminUsers: [] };
  oldUserList.forEach(u => {
    if (!newUserList.find(nu => nu.id === u.id)) {
      payload.toDeleteImpersonatedAdminUsers.push(u.id);
    }
  });

  newUserList.forEach(nu => {
    if (!oldUserList.find(u => u.id === nu.id)) {
      payload.impersonatedAdminUsers.push(nu.id);
    }
  });

  return payload;
};
